import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import useCompanies from "../hooks/useCompanies";

export default function PortfolioPanel({ selectedCompany, setSelectedCompany }) {
  const [userData, setUserData] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: "value", direction: "desc" });

  const companies = useCompanies();

  useEffect(() => {
    let mounted = true;
    const fetchPortfolio = async () => {
      if (!auth.currentUser) return;
      setLoading(true);
      try {
        const uid = auth.currentUser.uid;
        const userDocRef = doc(db, "Users", uid);
        const userSnap = await getDoc(userDocRef);
        if (!mounted) return;
        if (!userSnap.exists()) {
          setUserData(null);
          setHoldings([]);
          return;
        }

        const data = userSnap.data();
        setUserData(data);

        const extracted = [];
        for (const [key, val] of Object.entries(data)) {
          if (key.startsWith("company") && typeof val === "number" && val >= 1) {
            const ticker = key.slice(7);
            extracted.push({ keyName: key, ticker, volume: val });
          }
        }
        if (Number(data.userGold) >= 1) {
          extracted.push({ keyName: "userGold", ticker: "XAU", volume: Number(data.userGold) });
        }

        const mapped = extracted.map((h) => {
          const company = companies.find((c) => c.companyTicker === h.ticker) || null;
          const price = company?.companyStockPrice ?? company?.companyPrice ?? 0;
          const value = Number(price) * Number(h.volume || 0);
          return { ...h, company, price, value };
        });

        setHoldings(mapped);
      } catch (err) {
        console.error("Error fetching portfolio:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchPortfolio();
    return () => { mounted = false; };
  }, [companies]);

  const fmtMoney = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedHoldings = [...holdings].sort((a, b) => {
    if (sortConfig.key) {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toUpperCase();
      if (typeof valB === "string") valB = valB.toUpperCase();
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  if (loading) return <p style={{ color: "#50e3c2", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.75rem" }} className="blink">LOADING...</p>;
  if (!userData) return <p style={{ color: "#ff7b72", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.75rem" }}>Nothing found.</p>;

  const portfolioValue = Number(userData.userPortfolioValue ?? 0);
  const usd = Number(userData.userMoney ?? 0);
  const holdingsSummary = portfolioValue - usd;

  const summaryRows = [
    { label: "TOTAL NET WORTH", value: portfolioValue, color: "#50e3c2", bold: true },
    { label: "TOTAL HOLDINGS", value: holdingsSummary, color: "#eee" },
    { label: "CASH BALANCE", value: usd, color: "#eee" },
    { label: "FOREX HOLDINGS", value: userData.userForex ?? 0 },
    { label: "STOCK EQUITY", value: userData.userStocks ?? 0 },
    { label: "CRYPTO ASSETS", value: userData.userCrypto ?? 0 },
    { label: "GOLD RESERVES", value: userData.userGoldValue ?? 0 },
    { label: "BOND HOLDINGS", value: userData.userBonds ?? 0 },
    { label: "FUTURES POS.", value: userData.userFutures ?? 0 },
  ];

  return (
    <div className="portfolio-panel" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.7rem", color: "#eee" }}>
      
      {/* Detailed Summary Table */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "1.5rem" }}>
        <tbody>
          {summaryRows.map((row, idx) => (
            <tr key={row.label} style={{ background: idx % 2 === 0 ? "rgba(255,255,255,0.02)" : "transparent" }}>
              <th style={{ 
                textAlign: "left", 
                padding: "4px 8px", 
                color: row.color || "#888", 
                fontWeight: row.bold ? "bold" : "normal",
                borderBottom: "1px solid rgba(80, 227, 194, 0.1)"
              }}>
                {row.label}
              </th>
              <td style={{ 
                textAlign: "right", 
                padding: "4px 8px", 
                color: row.color || "#eee",
                borderBottom: "1px solid rgba(80, 227, 194, 0.1)",
                fontVariantNumeric: "tabular-nums"
              }}>
                {fmtMoney(row.value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Assets Breakdown Table */}
      <div style={{ padding: "4px 8px", color: "#50e3c2", fontWeight: "bold", borderBottom: "1px solid #50e3c2", marginBottom: "4px" }}>
        TOTAL HOLDINGS
      </div>
      
      {holdings.length === 0 ? (
        <p style={{ padding: "10px", color: "#555" }}>No holdings found.</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ color: "#888", textAlign: "left", fontSize: "0.6rem" }}>
              {["ticker", "price", "volume", "value"].map((key) => (
                <th 
                  key={key} 
                  onClick={() => handleSort(key)}
                  style={{ cursor: "pointer", padding: "8px", textTransform: "uppercase" }}
                >
                  {key}{sortConfig.key === key ? (sortConfig.direction === "asc" ? " ◢" : " ◣") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedHoldings.map(({ keyName, ticker, price, volume, value, company }) => {
              const isSelected = selectedCompany?.companyTicker === ticker || selectedCompany === ticker;
              return (
                <tr
                  key={keyName}
                  onClick={() => company && setSelectedCompany(company)}
                  style={{
                    cursor: company ? "pointer" : "default",
                    background: isSelected ? "rgba(80, 227, 194, 0.15)" : "transparent",
                    color: isSelected ? "#50e3c2" : "#eee",
                    transition: "background 0.1s"
                  }}
                >
                  <td style={{ padding: "8px", fontWeight: "bold" }}>{ticker}</td>
                  <td style={{ padding: "8px" }}>{fmtMoney(price)}</td>
                  <td style={{ padding: "8px", color: "#888" }}>{volume.toLocaleString()}</td>
                  <td style={{ padding: "8px", textAlign: "right" }}>{fmtMoney(value)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}